import React, { MouseEventHandler } from 'react';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import { User } from '@lgg/isomorphic/types/__generated__/graphql';
import { LggOptionsDropdownButtonWithCustomTriggerControlled } from 'src/components/general/button/dropdown-button';
import { AssigneeLabel } from 'src/components/general/display/assignee-label';
import { ColoredTag, ColoredTagTheme } from 'src/components/general/display/colored-tag';
import { Table, TableColumns } from 'src/components/general/display/table';
import { DrawerOption } from 'src/components/general/drawer/bottom/options-bottom-drawer';
import { InlineFormDeleteOption } from 'src/components/general/modals/inline-form/shared';
import {
  getDefaultSortOrder,
  MoreOptionsIcon,
  TableSortData,
} from 'src/components/general/table-helpers';
import { useFormatDate } from 'src/hooks/use-format-date';

const CreatedByLabel = styled(AssigneeLabel)`
  .assignee-label-avatar {
    height: 30px;
    width: 30px;
  }
`;

export const getCreatedByColumn = <T extends { createdBy: User }>(params: {
  title: string;
  sortData: TableSortData;
  testId: string;
}): TableColumns<T> => {
  const { title, sortData, testId } = params;

  return {
    title,
    key: 'createdBy',
    width: '240px',
    showSorterTooltip: false,
    sorter: true,
    defaultSortOrder: getDefaultSortOrder(
      sortData.key === 'createdBy',
      sortData.direction,
    ),
    render: (_, { createdBy }) => (
      <CreatedByLabel assignee={createdBy} data-lgg-id={testId} />
    ),
  };
};

export const TableLightText = styled.span`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 13px;
  line-height: 16px;
`;

const CreatedAtCell = ({ date, testId }: { date: string; testId: string }) => {
  const { formatDate, formatSimpleDate } = useFormatDate();

  return (
    <TableLightText data-lgg-id={testId}>
      {`${formatSimpleDate(date)}, ${formatDate(date, 'h:mm A')}`}
    </TableLightText>
  );
};

export const getCreatedAtColumn = <T extends { createdAt: string }>(params: {
  title: string;
  sortData: TableSortData;
  testId: string;
  width: string;
}): TableColumns<T> => {
  const { title, sortData, testId, width } = params;

  return {
    title,
    key: 'createdAt',
    width,
    showSorterTooltip: false,
    sorter: true,
    defaultSortOrder: getDefaultSortOrder(
      sortData.key === 'createdAt',
      sortData.direction,
    ),
    render: (_, { createdAt }) => <CreatedAtCell date={createdAt} testId={testId} />,
  };
};

export const getRowOptionsColumn = <T,>(
  getOptions: (record: T) => DrawerOption[],
): TableColumns<T> => ({
  key: 'more-options',
  showSorterTooltip: false,
  width: '62px',
  wrapContent: true,
  render: (_, record) => (
    <LggOptionsDropdownButtonWithCustomTriggerControlled
      customTrigger={
        <MoreOptionsIcon type="moreOptions" lggTestId="table-cell-more-options" />
      }
      customDropdownProps={{ getPopupContainer: (trigger) => trigger }}
      options={getOptions(record)}
    />
  ),
});

export const getDeleteRowOption = (params: {
  label: string;
  testId: string;
  onClick: MouseEventHandler;
}): DrawerOption => {
  const { label, testId, onClick } = params;

  return {
    label,
    render: () => (
      <InlineFormDeleteOption
        label={label}
        icon="deleteBin"
        data-lgg-id={testId}
        onClick={onClick}
      />
    ),
  };
};

const STATUS_TAG_THEMES: Record<'READY' | 'PROCESSING' | 'FAILED', ColoredTagTheme> = {
  READY: {
    color: 'secondaryMintDark',
    border: 'secondaryMint60',
    background: 'secondaryMint10',
  },
  PROCESSING: {
    color: 'secondaryGoldDark',
    border: 'secondaryGold60',
    background: 'secondaryGold10',
  },
  FAILED: {
    color: 'secondaryCoralDark',
    border: 'secondaryCoral60',
    background: 'secondaryCoral10',
  },
};

const StatusTag = styled(ColoredTag)`
  height: auto;
  margin: 0;
  padding: 1px 4px;
`;

export const KnowledgeSourceStatusTag = ({
  status,
  label,
  'data-lgg-id': testId,
}: {
  status: 'READY' | 'PROCESSING' | 'UPDATING' | 'FAILED';
  label: string;
  'data-lgg-id'?: string;
}) => (
  <StatusTag
    tagTheme={STATUS_TAG_THEMES[status === 'UPDATING' ? 'PROCESSING' : status]}
    data-lgg-id={testId}
  >
    {label}
  </StatusTag>
);

export const KnowledgeSourceTableFrame = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.porcelain2};
  border-radius: 10px;
  overflow: hidden;
`;

export const KnowledgeSourceTable = styled(Table)`
  ${up('md')} {
    && .ant-table-thead {
      height: 50px;

      .ant-table-cell {
        line-height: 14px;
        padding: 0 0 0 30px;

        &:last-child {
          padding: 0 23px;
        }
      }

      .ant-table-column-sorters {
        padding: 0;
      }
    }

    && .ant-table-tbody .ant-table-row {
      height: 60px;

      .ant-table-cell {
        padding: 0 0 0 30px;

        &:last-child {
          padding: 0 23px;
        }
      }
    }
  }
` as typeof Table;
